import React, { useState, useEffect } from 'react';
import LoginForm from './LoginForm';
import RegistrationFlow from './RegistrationFlow';
import ClientDashboard from './ClientDashboard';
import ClientInterests from './ClientInterests';
import OrganizerTutorial from './OrganizerTutorial';
import AdminLogin from './AdminLogin';
import Home from './Home';
import SingleCard from './SingleCard';
import PaymentSuccess from './PaymentSuccess';
import PaymentFailure from './PaymentFailure';
import PaymentPending from './PaymentPending';
import { updateClientInterestsLegacy, markAsNotFirstLogin, getCurrentUser, getCurrentToken, clearUserSession, UserDataLegacy } from '../utils/userStorage';
import { getClasses, ClassData } from '../services/api';
import { getApiBaseUrl } from '../config/apiConfig';
import { scrollToTop } from '../hooks/useScrollToTop';
import { consumePendingAuthIntent, getPendingAuthIntent } from '../utils/checkoutFlow';

type AuthStep = 'home' | 'login' | 'register' | 'client-interests' | 'organizer-tutorial' | 'dashboard' | 'admin-login' | 'admin-dashboard' | 'single-card' | 'payment-success' | 'payment-failure' | 'payment-pending';

const AuthFlow: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<AuthStep>('home');
  const [userData, setUserData] = useState<UserDataLegacy | null>(null);
  const [selectedPackId, setSelectedPackId] = useState<string | null>(null);
  const [selectedPack, setSelectedPack] = useState<ClassData | null>(null);
  const [shouldAutoplay, setShouldAutoplay] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);

  // Verificar sesión existente al cargar la aplicación
  useEffect(() => {
    const checkExistingSession = () => {
      try {
        // Esperar un momento para asegurar que localStorage esté disponible
        // y verificar la sesión de forma más robusta
        const verifySession = () => {
          // Primero intentar leer directamente del localStorage para debug
          const sessionData = localStorage.getItem('peekpass-session');
          const tokenData = localStorage.getItem('peekpass-token');
          const userData = localStorage.getItem('peekpass-user');

          console.log('🔍 Verificando sesión al cargar:', {
            sessionExists: !!sessionData,
            tokenExists: !!tokenData,
            userExists: !!userData
          });

          // Usar la función getCurrentUser que maneja toda la lógica
          const currentUser = getCurrentUser();

	          if (currentUser) {
            console.log('✅ Sesión encontrada, usuario:', currentUser.email);
            // Convertir UserData a UserDataLegacy
            const legacyUser: UserDataLegacy = {
              ...currentUser,
              password: '', // No tenemos la contraseña en la sesión
              createdAt: new Date(currentUser.createdAt)
            };
            setUserData(legacyUser);
	            const path = window.location.pathname;
	            if (path === '/dashboard' && currentUser.role === 'admin') {
	              setCurrentStep('admin-dashboard');
	            } else if (path === '/dashboard') {
	              setCurrentStep('dashboard');
	            } else {
	              setCurrentStep('home');
	            }
	          } else {
	            console.log('ℹ️ No hay sesión activa');
	            const authMode = new URLSearchParams(window.location.search).get('auth');
	            if (authMode === 'register') {
	              setCurrentStep('register');
	            } else if (authMode === 'login' || getPendingAuthIntent()) {
	              setCurrentStep('login');
	            } else {
	              setCurrentStep('home');
	            }
	          }
        };

        // Pequeño delay para asegurar que localStorage esté disponible
        if (typeof window !== 'undefined' && window.localStorage) {
          verifySession();
        } else {
          // Si localStorage no está disponible aún, esperar un poco
          setTimeout(verifySession, 100);
        }
      } catch (error) {
        console.error('❌ Error al verificar sesión:', error);
        // 🌐 ERROR EN SESIÓN - PERMITIR NAVEGACIÓN LIBRE
        setCurrentStep('home');
      } finally {
        setIsLoading(false);
      }
    };

    checkExistingSession();
  }, []);

  // Manejar rutas de pago y errores de OAuth
  useEffect(() => {
    const handlePaymentRoutes = () => {
      const path = window.location.pathname;
      const search = window.location.search;
      const urlParams = new URLSearchParams(search);

      // Manejar rutas de pago
      if (path === '/payment/success') {
        setCurrentStep('payment-success');
      } else if (path === '/payment/failure') {
        setCurrentStep('payment-failure');
      } else if (path === '/payment/pending') {
        setCurrentStep('payment-pending');
      }
      // Manejar ruta de dashboard (incluye errores de OAuth)
      else if (path === '/dashboard') {
        // Verificar si hay usuario autenticado
        const currentUser = getCurrentUser();
        if (currentUser) {
          // Convertir UserData a UserDataLegacy
          const legacyUser: UserDataLegacy = {
            ...currentUser,
            password: '',
            createdAt: new Date(currentUser.createdAt)
          };
          setUserData(legacyUser);

          // Redirigir al dashboard según el rol
          if (currentUser.role === 'admin') {
            setCurrentStep('admin-dashboard');
          } else {
            setCurrentStep('dashboard');
          }

          // Manejar errores de OAuth en la URL
          const oauthError = urlParams.get('error');
          const oauthDetails = urlParams.get('details');
          const oauthFullDetails = urlParams.get('fullDetails');

          if (oauthError === 'oauth_failed' || oauthError === 'oauth_missing_params' || oauthError === 'oauth_invalid_state' || oauthError === 'user_not_found') {
            const errorMessage = oauthDetails
              ? decodeURIComponent(oauthDetails)
              : 'Error desconocido al conectar con MercadoPago';

            const fullDetails = oauthFullDetails
              ? decodeURIComponent(oauthFullDetails)
              : errorMessage;

            console.error('❌ Error de OAuth:', errorMessage);
            console.error('❌ Tipo de error:', oauthError);
            console.error('❌ Detalles completos:', fullDetails);

            // Construir mensaje de error más detallado según el tipo
            let errorDescription = errorMessage;
            let possibleCauses = [
              'El código de autorización ya fue usado (intenta vincular nuevamente)',
              'La URL de redirección no coincide con la configurada en MercadoPago',
              'Credenciales de MercadoPago incorrectas o expiradas',
              'Error de conexión con MercadoPago',
              'El token de autorización expiró'
            ];

            // Personalizar según el tipo de error
            if (oauthError === 'oauth_missing_params') {
              errorDescription = 'Faltan parámetros de autorización. Por favor, intenta vincular nuevamente.';
              possibleCauses = [
                'Faltan parámetros en la respuesta de MercadoPago',
                'Intenta vincular nuevamente desde el inicio',
                'Si el problema persiste, contacta al administrador'
              ];
            } else if (oauthError === 'oauth_invalid_state') {
              errorDescription = 'El estado de autorización es inválido o expiró. Por favor, intenta vincular nuevamente.';
              possibleCauses = [
                'El estado de autorización expiró (más de 10 minutos)',
                'Intenta vincular nuevamente desde el inicio',
                'No refreshes la página durante el proceso de autorización'
              ];
            } else if (oauthError === 'user_not_found') {
              errorDescription = 'Usuario no encontrado. Por favor, inicia sesión nuevamente.';
              possibleCauses = [
                'Tu sesión expiró o fue invalidada',
                'Inicia sesión nuevamente e intenta vincular',
                'Si el problema persiste, contacta al administrador'
              ];
            } else if (errorMessage.includes('código') || errorMessage.includes('inválido') || errorMessage.includes('usado')) {
              possibleCauses = [
                'El código de autorización ya fue usado o expiró',
                'Intenta vincular nuevamente desde el inicio',
                ...possibleCauses.slice(2)
              ];
            } else if (errorMessage.includes('redirect_uri') || errorMessage.includes('redirección')) {
              possibleCauses = [
                'La URL de redirección no coincide',
                'Verifica la configuración de MercadoPago en el servidor',
                ...possibleCauses.slice(2)
              ];
            } else if (errorMessage.includes('Credenciales')) {
              possibleCauses = [
                'Las credenciales de MercadoPago son inválidas',
                'Verifica la configuración del servidor',
                ...possibleCauses.slice(2)
              ];
            }

            // Mostrar mensaje de error con opción de reintentar
            const shouldRetry = window.confirm(
              `❌ Error al vincular con MercadoPago\n\n` +
              `Mensaje: ${errorDescription}\n\n` +
              `Detalles técnicos: ${fullDetails}\n\n` +
              `Posibles causas:\n${possibleCauses.map(c => `• ${c}`).join('\n')}\n\n` +
              `¿Deseas intentar vincular nuevamente con MercadoPago?`
            );

            // Limpiar URL primero
            window.history.replaceState({}, document.title, '/dashboard');

            // Si el usuario quiere reintentar, redirigir a MercadoPago
            if (shouldRetry) {
              console.log('🔄 Reintentando vinculación con MercadoPago...');
              handleRetryMercadoPagoConnection();
            }
          }
          // Manejar éxito de OAuth
          else if (urlParams.get('mercadopago') === 'success') {
            console.log('✅ OAuth de MercadoPago completado exitosamente');
            // El manejo del éxito se hace en ClientDashboard
          }
        } else {
          // No hay usuario autenticado, ir al login
          setCurrentStep('login');
        }
      }
    };

    handlePaymentRoutes();
  }, []);

	  const handleLoginSuccess = (user: UserDataLegacy) => {
	    setUserData(user);
	    const pendingIntent = consumePendingAuthIntent();
	    if (pendingIntent?.returnTo) {
	      window.location.href = pendingIntent.returnTo;
	      return;
	    }

	    if (user.role === 'admin') {
	      setCurrentStep('admin-dashboard');
	    } else {
	      setCurrentStep('home');
	    }
	  };

  const handleGoToRegister = () => {
    setCurrentStep('register');
  };

  const handleGoToLogin = () => {
    setCurrentStep('login');
  };

  const handleGoToAdminLogin = () => {
    setCurrentStep('admin-login');
  };

  const handleGoToUserLogin = () => {
    setCurrentStep('login');
  };


  const handleLogout = () => {
    try {
      clearUserSession();
      localStorage.removeItem('peekpass-session');
      localStorage.removeItem('peekpass-token');
      localStorage.removeItem('peekpass-refresh');
      document.cookie = 'peekpass-session=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
      document.cookie = 'peekpass-token=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
      document.cookie = 'peekpass-refresh=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
      setUserData(null);
      setCurrentStep('home');
    } catch (error) {
      console.error('❌ Error durante el logout:', error);
    }
  };

  const handleClientInterestsComplete = (interests: string[]) => {
    if (userData) {
      // Actualizar en la base de datos
      updateClientInterestsLegacy(userData.id, interests);

      // Actualizar estado local
      setUserData({ ...userData, interests, isFirstLogin: false });
      setCurrentStep('dashboard');
    }
  };

  const handleOrganizerTutorialComplete = () => {
    if (userData) {
      // Actualizar en la base de datos
      markAsNotFirstLogin(userData.id);

      // Actualizar estado local
      setUserData({ ...userData, isFirstLogin: false });
      setCurrentStep('dashboard');
    }
  };

  // 🔄 Reintentar conexión con MercadoPago cuando hay error de OAuth
  const handleRetryMercadoPagoConnection = async () => {
    try {
      console.log('🔄 Reintentando vinculación con MercadoPago...');

      // Obtener token de autenticación
      const token = getCurrentToken();
      if (!token) {
        alert('❌ No estás autenticado. Por favor, inicia sesión nuevamente.');
        setCurrentStep('login');
        return;
      }

      // Obtener URL de OAuth desde el backend
      const API_BASE_URL = getApiBaseUrl();
      const response = await fetch(`${API_BASE_URL}/auth/mercadopago/connect`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al conectar con MercadoPago');
      }

      // Obtener la URL de redirección
      const data = await response.json();
      if (data.redirectUrl) {
        console.log('✅ Redirigiendo a MercadoPago para autorización:', data.redirectUrl);
        // Redirigir a MercadoPago para autorización
        window.location.href = data.redirectUrl;
      } else {
        throw new Error('No se recibió URL de redirección');
      }

    } catch (error: any) {
      console.error('❌ Error reintentando vinculación con MercadoPago:', error);
      alert(`❌ Error al vincular con MercadoPago: ${error.message}\n\nPor favor, intenta nuevamente desde el dashboard.`);
    }
  };

  const handleViewPackDetails = async (packId: string) => {
    try {
      if (packId === 'cart') {
        // Redirigir al carrito (SingleCard en modo carrito)
        setSelectedPackId('cart');
        setSelectedPack(null); // No hay pack específico para el carrito
        setShouldAutoplay(false);
        scrollToTop();
        setCurrentStep('single-card');
        return;
      }

      // Detectar si hay parámetro de autoplay
      const shouldAutoPlay = packId.includes('?autoplay=true');
      const cleanPackId = packId.split('?')[0]; // Remover parámetros de query

      setSelectedPackId(cleanPackId);
      setShouldAutoplay(shouldAutoPlay);

      console.log('🎬 AuthFlow: Navegando a SingleCard', {
        packId: cleanPackId,
        shouldAutoplay,
        originalPackId: packId
      });

      // Obtener el pack específico desde la API
      const response = await getClasses({ limit: 1000 }); // Obtener todos los eventos
      const pack = response.data.find((p: any) => p._id === cleanPackId || p.title === cleanPackId);
      if (pack) {
        setSelectedPack(pack);
        scrollToTop();
        setCurrentStep('single-card');
      } else {
        console.error('Pack no encontrado:', cleanPackId);
        // Volver al home si no se encuentra el pack
        setCurrentStep('home');
      }
    } catch (error) {
      console.error('Error obteniendo pack:', error);
      setCurrentStep('home');
    }
  };

  const handleGoBackToHome = () => {
    setSelectedPackId(null);
    setSelectedPack(null);
    setShouldAutoplay(false);
    scrollToTop();
    // Verificar y mantener la sesión al volver al Home
    const currentUser = getCurrentUser();
    if (currentUser) {
      const legacyUser: UserDataLegacy = {
        ...currentUser,
        password: '',
        createdAt: new Date(currentUser.createdAt)
      };
      setUserData(legacyUser);
      console.log('✅ Manteniendo sesión al volver al Home:', currentUser.email);
    }
    setCurrentStep('home');
  };

  const handlePurchasePack = (packId: string) => {
    // Aquí se podría actualizar el estado del usuario con la compra
    console.log('Pack comprado:', packId);
    // Por ahora, volvemos al home
    setCurrentStep('home');
  };

	  const handleRegistrationComplete = (user: UserDataLegacy) => {
	    console.log('🎉 AuthFlow: Registro completado, redirigiendo al home...');
    console.log('👤 Datos del usuario:', user);

    // Después del registro, el usuario va directamente al home
	    try {
	      setUserData(user);
	      const pendingIntent = consumePendingAuthIntent();
	      if (pendingIntent?.returnTo) {
	        window.location.href = pendingIntent.returnTo;
	        return;
	      }
	      setCurrentStep('home');
	      console.log('✅ AuthFlow: Redirección completada, usuario en home');
	    } catch (error) {
      console.error('❌ Error en redirección:', error);
      // Fallback: intentar redirección nuevamente
      setCurrentStep('home');
    }
  };

  // Mostrar loading mientras se verifica la sesión
  if (isLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-zen-50 to-zen-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-zen-600 mx-auto mb-4"></div>
          <p className="text-sage-600">Cargando...</p>
        </div>
      </div>
    );
  }

  switch (currentStep) {
    case 'home':
      // 🌐 PERMITIR ACCESO LIBRE AL HOME (SIN AUTENTICACIÓN REQUERIDA)
      return (
        <Home
          onLogin={handleGoToLogin}
          onRegister={handleGoToRegister}
          userEmail={userData?.email}
          onViewPackDetails={handleViewPackDetails}
          onGoToDashboard={() => {
            // Redirigir al dashboard según el rol del usuario
            if (userData?.role === 'admin') {
              setCurrentStep('admin-dashboard');
            } else {
              // Todos los usuarios autenticados (client/organizer) van al mismo dashboard
              setCurrentStep('dashboard');
            }
          }}
        />
      );

    case 'login':
      return (
        <LoginForm
          onLoginSuccess={handleLoginSuccess}
          onGoToRegister={handleGoToRegister}
          onGoToAdminLogin={handleGoToAdminLogin}
        />
      );

    case 'admin-login':
      return (
        <AdminLogin
          onLoginSuccess={handleLoginSuccess}
          onGoToUserLogin={handleGoToUserLogin}
        />
      );

    case 'admin-dashboard':
      return (
        <ClientDashboard
          userData={userData!}
          onLogout={handleLogout}
          onGoHome={handleGoBackToHome}
        />
      );

    case 'single-card':
      // 🌐 PERMITIR ACCESO LIBRE A SINGLE CARD (SIN AUTENTICACIÓN REQUERIDA)
      if (!selectedPack) {
        return (
          <Home
            onLogin={handleGoToLogin}
            onRegister={handleGoToRegister}
            userEmail={userData?.email}
            onViewPackDetails={handleViewPackDetails}
            onGoToDashboard={() => {
              // Redirigir al dashboard según el rol del usuario
              if (userData?.role === 'admin') {
                setCurrentStep('admin-dashboard');
              } else if (userData?.role === 'organizer') {
                setCurrentStep('dashboard');
              } else if (userData?.role === 'client') {
                setCurrentStep('dashboard');
              } else {
                // Si no hay usuario autenticado, ir al login
                setCurrentStep('login');
              }
            }}
          />
        );
      }
      return (
        <SingleCard
          pack={selectedPack as any}
          userEmail={userData?.email}
          onGoBack={handleGoBackToHome}
          onPurchase={handlePurchasePack}
          onRegister={handleGoToRegister}
          isCartMode={selectedPackId === 'cart'}
          shouldAutoplay={shouldAutoplay}
        />
      );

    case 'register':
      return (
        <RegistrationFlow
          onGoToLogin={handleGoToLogin}
          onRegistrationComplete={handleRegistrationComplete}
        />
      );

    case 'client-interests':
      return (
        <ClientInterests
          onComplete={handleClientInterestsComplete}
          onSkip={() => handleClientInterestsComplete([])}
        />
      );

    case 'organizer-tutorial':
      return (
        <OrganizerTutorial
          onComplete={handleOrganizerTutorialComplete}
          onSkip={handleOrganizerTutorialComplete}
        />
      );

    case 'dashboard':
      // Todos los usuarios autenticados (client/organizer/admin) usan el ClientDashboard unificado
      return (
        <ClientDashboard
          userData={userData!}
          onLogout={handleLogout}
          onGoHome={handleGoBackToHome}
        />
      );

    case 'payment-success':
      return <PaymentSuccess />;

    case 'payment-failure':
      return <PaymentFailure />;

    case 'payment-pending':
      return <PaymentPending />;

    default:
      // 🌐 CASO DEFAULT - PERMITIR NAVEGACIÓN LIBRE
      return (
        <Home
          onLogin={handleGoToLogin}
          onRegister={handleGoToRegister}
          userEmail={userData?.email}
          onViewPackDetails={handleViewPackDetails}
          onGoToDashboard={() => {
            // Redirigir al dashboard según el rol del usuario
            if (userData?.role === 'admin') {
              setCurrentStep('admin-dashboard');
            } else {
              // Todos los usuarios autenticados (client/organizer) van al mismo dashboard
              setCurrentStep('dashboard');
            }
          }}
        />
      );
  }
};

export default AuthFlow;
